import React from 'react';
import { useLocation } from 'react-router-dom';
import {
  HELP_ARTICLES,
  filterHelpArticles,
  getHelpTopicsWithCounts,
  getHelpArticleBySlug,
  type HelpTopic,
} from '../help/helpArticles';
import { renderMarkdown, parseInline } from '../help/markdownRenderer';
import {
  Search,
  X,
  ChevronRight,
  ChevronDown,
  LayoutDashboard,
  Wallet,
  CreditCard,
  PiggyBank,
  Target,
  TrendingUp,
  FileText,
  Settings,
} from 'lucide-react';

function getTopicIcon(topic: HelpTopic): React.ComponentType<{ className?: string }> {
  switch (topic) {
    case 'Dashboard':
      return LayoutDashboard;
    case 'Accounts':
      return Wallet;
    case 'Transactions':
      return CreditCard;
    case 'Budgets':
      return PiggyBank;
    case 'Goals':
      return Target;
    case 'Insights':
      return TrendingUp;
    case 'Reports':
      return FileText;
    case 'Settings':
      return Settings;
    default:
      return LayoutDashboard;
  }
}

export function HelpCenterPage(): React.ReactElement {
  const location = useLocation();
  const initialQuery = (location.state as { searchQuery?: string })?.searchQuery ?? '';
  const [query, setQuery] = React.useState(initialQuery);
  const [selectedTopic, setSelectedTopic] = React.useState<HelpTopic>('Dashboard');
  const [selectedArticleSlug, setSelectedArticleSlug] = React.useState<string | null>(null);
  const [isTopicDropdownOpen, setIsTopicDropdownOpen] = React.useState(false);
  const [expandedTopics, setExpandedTopics] = React.useState<Set<HelpTopic>>(new Set());

  // Clear location state after reading it
  React.useEffect(() => {
    if (initialQuery) {
      // Clear the state to prevent re-applying on re-renders
      window.history.replaceState({}, document.title);
    }
  }, [initialQuery]);

  const topics = React.useMemo(() => {
    return getHelpTopicsWithCounts(HELP_ARTICLES).filter(
      ({ topic }) => topic !== 'All Topics'
    );
  }, []);

  const filteredArticles = React.useMemo(() => {
    return filterHelpArticles({ articles: HELP_ARTICLES, topic: selectedTopic, query });
  }, [query, selectedTopic]);

  // Get Settings subpages
  const settingsSubpages = React.useMemo(() => {
    return HELP_ARTICLES.filter(article => 
      article.topic === 'Settings' && 
      article.slug.startsWith('settings-') &&
      article.slug !== 'settings'
    ).sort((a, b) => a.title.localeCompare(b.title));
  }, []);

  const toggleTopicExpansion = (topic: HelpTopic) => {
    setExpandedTopics(prev => {
      const next = new Set(prev);
      if (next.has(topic)) {
        next.delete(topic);
      } else {
        next.add(topic);
      }
      return next;
    });
  };

  const selectedArticle = selectedArticleSlug ? getHelpArticleBySlug(selectedArticleSlug) : null;

  // Clear selected article when topic or query changes
  React.useEffect(() => {
    setSelectedArticleSlug(null);
  }, [selectedTopic, query]);

  // Close dropdown when topic changes
  React.useEffect(() => {
    setIsTopicDropdownOpen(false);
  }, [selectedTopic]);

  // Close dropdown on Escape key
  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isTopicDropdownOpen) {
        setIsTopicDropdownOpen(false);
      }
    };

    if (isTopicDropdownOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isTopicDropdownOpen]);

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100">
          Help Center
        </h1>
        <p className="mt-3 text-gray-600 dark:text-gray-400">
          Find answers to your questions about managing your finances with MoneyMojo
        </p>

        {/* Search */}
        <div className="mt-6 max-w-2xl mx-auto">
          <label className="sr-only" htmlFor="help-search">
            Search help articles
          </label>
          <div className="relative">
            <Search
              className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400"
              aria-hidden="true"
            />
            <input
              id="help-search"
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search for help articles, features, or topics..."
              className={`w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 py-3 pl-12 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                query ? 'pr-12' : 'pr-4'
              }`}
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery('')}
                className="absolute right-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded"
                aria-label="Clear search"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Sidebar */}
        <aside className="lg:col-span-4 space-y-6">
          <nav 
            className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4"
            aria-label="Help topics navigation"
          >
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3 hidden lg:block">
              Browse by Topic
            </h2>
            
            {/* Desktop Navigation (lg+) - Sidebar List */}
            <ul className="hidden lg:block space-y-2" role="list">
              {topics.map(({ topic, count }) => {
                const active = topic === selectedTopic;
                const Icon = getTopicIcon(topic);
                const topicDescription = `View ${count} ${count === 1 ? 'article' : 'articles'} about ${topic}`;
                const isExpanded = expandedTopics.has(topic);
                const hasSubpages = topic === 'Settings' && settingsSubpages.length > 0;
                
                return (
                  <li key={topic} role="listitem" className="space-y-1">
                    <div className="flex items-center">
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedTopic(topic);
                          setQuery('');
                          setSelectedArticleSlug(null); // Clear selected article when switching topics
                        }}
                        className={`flex-1 flex items-center justify-between rounded-lg px-4 py-3 text-sm transition-colors duration-200 text-left ${
                          active
                            ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 font-semibold'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                        }`}
                        aria-label={topicDescription}
                        aria-current={active ? 'page' : undefined}
                      >
                        <span className="flex items-center space-x-3">
                          <Icon className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
                          <span className="text-sm font-medium">{topic}</span>
                        </span>
                        <span className="ml-3 inline-flex min-w-8 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700 px-2 py-0.5 text-xs font-medium text-gray-600 dark:text-gray-300">
                          {count}
                        </span>
                      </button>
                      {hasSubpages && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleTopicExpansion(topic);
                          }}
                          className={`ml-1 p-1 rounded transition-colors ${
                            active
                              ? 'text-primary-700 dark:text-primary-400 hover:bg-primary-100 dark:hover:bg-primary-900/30'
                              : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                          }`}
                          aria-label={isExpanded ? 'Collapse Settings subpages' : 'Expand Settings subpages'}
                          aria-expanded={isExpanded}
                        >
                          <ChevronDown 
                            className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                            aria-hidden="true"
                          />
                        </button>
                      )}
                    </div>
                    {hasSubpages && isExpanded && (
                      <ul className="ml-4 space-y-1 border-l-2 border-gray-200 dark:border-gray-700 pl-4" role="list">
                        {settingsSubpages.map((subpage) => {
                          const isSubpageActive = selectedArticleSlug === subpage.slug;
                          return (
                            <li key={subpage.slug} role="listitem">
                              <button
                                type="button"
                                onClick={() => {
                                  setSelectedTopic('Settings');
                                  setSelectedArticleSlug(subpage.slug);
                                  setQuery('');
                                }}
                                className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                                  isSubpageActive
                                    ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 font-medium'
                                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                                }`}
                                aria-label={`View ${subpage.title}`}
                                aria-current={isSubpageActive ? 'page' : undefined}
                              >
                                {subpage.title}
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>

            {/* Mobile & Tablet Navigation - Dropdown */}
            <div className="lg:hidden">
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setIsTopicDropdownOpen(!isTopicDropdownOpen)}
                  className="w-full flex items-center justify-between px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                  aria-label="Select help topic"
                  aria-expanded={isTopicDropdownOpen}
                  aria-haspopup="listbox"
                >
                  <div className="flex items-center space-x-3">
                    {(() => {
                      const ActiveIcon = getTopicIcon(selectedTopic);
                      const activeTopicData = topics.find(t => t.topic === selectedTopic);
                      return (
                        <>
                          <ActiveIcon className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
                          <span className="text-sm font-medium">{selectedTopic}</span>
                          {activeTopicData && (
                            <span className="inline-flex min-w-8 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700 px-2 py-0.5 text-xs font-medium text-gray-600 dark:text-gray-300">
                              {activeTopicData.count}
                            </span>
                          )}
                        </>
                      );
                    })()}
                  </div>
                  <ChevronDown 
                    className={`w-5 h-5 flex-shrink-0 transition-transform ${isTopicDropdownOpen ? 'rotate-180' : ''}`}
                    aria-hidden="true"
                  />
                </button>

                {/* Dropdown Menu */}
                {isTopicDropdownOpen && (
                  <>
                    {/* Backdrop */}
                    <div 
                      className="fixed inset-0 z-10"
                      onClick={() => setIsTopicDropdownOpen(false)}
                      aria-hidden="true"
                    />
                    
                    {/* Dropdown List */}
                    <ul
                      className="absolute z-20 w-full mt-2 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden max-h-96 overflow-y-auto"
                      role="listbox"
                    >
                      {topics.map(({ topic, count }) => {
                        const Icon = getTopicIcon(topic);
                        const active = topic === selectedTopic;
                        const topicDescription = `View ${count} ${count === 1 ? 'article' : 'articles'} about ${topic}`;
                        const isExpanded = expandedTopics.has(topic);
                        const hasSubpages = topic === 'Settings' && settingsSubpages.length > 0;
                        return (
                          <li key={topic} role="option" aria-selected={active} className="space-y-1">
                            <div className="flex items-center">
                              <button
                                type="button"
                                onClick={() => {
                                  setSelectedTopic(topic);
                                  setQuery('');
                                  setSelectedArticleSlug(null); // Clear selected article when switching topics
                                  setIsTopicDropdownOpen(false);
                                }}
                                className={`flex-1 flex items-center justify-between px-4 py-3 text-left transition-colors ${
                                  active
                                    ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 font-semibold'
                                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                                }`}
                                aria-label={topicDescription}
                              >
                                <span className="flex items-center space-x-3">
                                  <Icon className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
                                  <span className="text-sm font-medium">{topic}</span>
                                </span>
                                <span className="ml-3 inline-flex min-w-8 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700 px-2 py-0.5 text-xs font-medium text-gray-600 dark:text-gray-300">
                                  {count}
                                </span>
                              </button>
                              {hasSubpages && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleTopicExpansion(topic);
                                  }}
                                  className={`ml-1 p-2 rounded transition-colors ${
                                    active
                                      ? 'text-primary-700 dark:text-primary-400 hover:bg-primary-100 dark:hover:bg-primary-900/30'
                                      : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                                  }`}
                                  aria-label={isExpanded ? 'Collapse Settings subpages' : 'Expand Settings subpages'}
                                  aria-expanded={isExpanded}
                                >
                                  <ChevronDown 
                                    className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                                    aria-hidden="true"
                                  />
                                </button>
                              )}
                            </div>
                            {hasSubpages && isExpanded && (
                              <ul className="ml-4 space-y-1 border-l-2 border-gray-200 dark:border-gray-700 pl-4" role="list">
                                {settingsSubpages.map((subpage) => {
                                  const isSubpageActive = selectedArticleSlug === subpage.slug;
                                  return (
                                    <li key={subpage.slug} role="listitem">
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setSelectedTopic('Settings');
                                          setSelectedArticleSlug(subpage.slug);
                                          setQuery('');
                                          setIsTopicDropdownOpen(false);
                                        }}
                                        className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                                          isSubpageActive
                                            ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 font-medium'
                                            : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                                        }`}
                                        aria-label={`View ${subpage.title}`}
                                        aria-current={isSubpageActive ? 'page' : undefined}
                                      >
                                        {subpage.title}
                                      </button>
                                    </li>
                                  );
                                })}
                              </ul>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </>
                )}
              </div>
            </div>
          </nav>
        </aside>

        {/* Main */}
        <main className="lg:col-span-8">
          {selectedArticle ? (
            // Show selected article
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <header className="mb-6">
                <button
                  type="button"
                  onClick={() => setSelectedArticleSlug(null)}
                  className="mb-4 text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
                >
                  ← Back to {query ? 'Search Results' : selectedTopic}
                </button>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                  {selectedArticle.title}
                </h1>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  {selectedArticle.tags.map((tag) => (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => setQuery(tag)}
                      className="inline-flex items-center rounded-full bg-gray-100 dark:bg-gray-700 px-2 py-0.5 text-xs text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 cursor-pointer transition-colors"
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              </header>

              <article>{renderMarkdown(selectedArticle.markdown ?? '')}</article>
            </div>
          ) : filteredArticles.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
                {query ? 'Search Results' : selectedTopic}
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {query
                  ? `No articles match "${query}". Try a different keyword.`
                  : 'No articles match your search. Try a different keyword or choose another topic.'}
              </p>
            </div>
          ) : filteredArticles.length === 1 && !query ? (
            // Single article (no search): render content directly
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <header className="mb-6">
                <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                  {filteredArticles[0]?.title}
                </h1>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  {filteredArticles[0]?.tags.map((tag) => (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => setQuery(tag)}
                      className="inline-flex items-center rounded-full bg-gray-100 dark:bg-gray-700 px-2 py-0.5 text-xs text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 cursor-pointer transition-colors"
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              </header>

              <article>{renderMarkdown(filteredArticles[0]?.markdown ?? '')}</article>
            </div>
          ) : (
            // Multiple articles or search results: show list
            <>
              <div className="mb-4">
                <div className="flex items-center gap-2">
                  <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                    {query ? 'Search Results' : selectedTopic}
                  </h2>
                  {query && (
                    <span className="inline-flex items-center rounded-full bg-primary-100 dark:bg-primary-900/20 px-2.5 py-0.5 text-xs font-medium text-primary-700 dark:text-primary-200">
                      {filteredArticles.length} {filteredArticles.length === 1 ? 'result' : 'results'}
                    </span>
                  )}
                </div>
                {!query && (
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    {filteredArticles.length} articles
                  </p>
                )}
              </div>

              <div className="space-y-3">
                {filteredArticles.map((article) => (
                  <button
                    key={article.slug}
                    type="button"
                    onClick={() => setSelectedArticleSlug(article.slug)}
                    className="group w-full text-left block bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 group-hover:text-primary-700 dark:group-hover:text-primary-300">
                          {article.title}
                        </h3>
                        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                          {parseInline(article.excerpt)}
                        </p>

                        <div className="mt-3 flex flex-wrap items-center gap-2">
                          {/* Show topic badge when search is active */}
                          {query && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setQuery('');
                                setSelectedTopic(article.topic);
                              }}
                              className="inline-flex items-center rounded-full bg-blue-100 dark:bg-blue-900/20 px-2 py-0.5 text-xs font-medium text-blue-700 dark:text-blue-200 hover:bg-blue-200 dark:hover:bg-blue-900/30 cursor-pointer transition-colors"
                            >
                              {article.topic}
                            </button>
                          )}
                          {article.tags.slice(0, 4).map((tag) => (
                            <button
                              key={tag}
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setQuery(tag);
                              }}
                              className="inline-flex items-center rounded-full bg-gray-100 dark:bg-gray-700 px-2 py-0.5 text-xs text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 cursor-pointer transition-colors"
                            >
                              {tag}
                            </button>
                          ))}
                        </div>
                      </div>
                      <ChevronRight className="mt-1 h-5 w-5 text-gray-300 dark:text-gray-600 group-hover:text-gray-500 dark:group-hover:text-gray-300 transition-colors flex-shrink-0" />
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  );
}


